import React from 'react';
import styled, { css } from 'styled-components';

type Props = {
  label: string | JSX.Element;
  level: number;
  weight: number;
  color?: string;
  margin?: string;
  withLines?: boolean;
  center?: boolean;
};

const getFontSize = ({
  level,
  mobile,
}: {
  level: number;
  mobile?: boolean;
}): string => {
  switch (level) {
    case 1:
      return mobile ? '1.5rem' : '1.875rem';
    case 2:
      return mobile ? '1.3rem' : '1.5rem';
    case 3:
      return mobile ? '0.9rem' : '1.1rem';
    case 4:
      return 'auto';
    case 5:
      return 'auto';
    default:
      return 'auto';
  }
};

const getMargin = ({
  level,
  mobile,
}: {
  level: number;
  mobile?: boolean;
}): string => {
  switch (level) {
    case 1:
      return mobile ? '2.5rem 0 1rem' : '1.875rem 0 ';
    case 2:
      return mobile ? '0.8rem 0 0.4rem' : '0.625rem 0';
    case 3:
      return mobile ? '0.8rem 0 0.4rem' : '0.625rem 0';
    case 4:
      return 'auto';
    case 5:
      return 'auto';
    default:
      return 'auto';
  }
};

export const Text = ({
  label,
  level,
  weight,
  color = '#154077',
  margin,
  withLines = false,
  center = false,
}: Props): JSX.Element => {
  return (
    <StyledHeading
      level={level}
      weight={weight}
      color={color}
      margin={margin}
      $withLines={withLines}
      $center={center}
    >
      <StyledText>{label}</StyledText>
    </StyledHeading>
  );
};

const StyledHeading = styled.div<{
  level: number;
  color: string;
  weight: number;
  margin?: string;
  $withLines?: boolean;
  $center?: boolean;
}>`
  font-size: ${({ level }) => getFontSize({ level })};
  margin: ${({ level, margin }) => margin || getMargin({ level })};
  color: ${({ color }) => color};
  font-weight: ${({ weight }) => weight};
  position: relative;
  user-select: none;
  height: max-content;
  text-wrap: wrap;
  text-align: ${({ $center }) => ($center ? 'center' : 'left')};

  ${({ $withLines, color }) =>
    $withLines &&
    css`
      &:after {
        content: '';
        height: 10%;
        top: 50%;
        left: 50%;
        transform: translateX(-50%);
        height: 3px;
        width: 100%;
        z-index: -2;
        background-color: ${color};
        position: absolute;
      }

      & > span {
        margin-left: 4rem;
        padding: 0 0.5rem;
      }
    `}

  @media (max-width: 640px) {
    font-size: ${({ level }) => getFontSize({ level, mobile: true })};
    margin: ${({ level }) => getMargin({ level, mobile: true })};
  }
`;

const StyledText = styled.span`
  background-color: #f1f1f1;
  z-index: -1;

  & > div > a {
    font-style: italic;
    text-decoration: underline;
  }
`;
